import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { Save, Upload, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const CMSHomepage = () => {
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/content`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Flatten the content for easier editing
        const flatContent = {};
        if (data.homepage) {
          Object.entries(data.homepage).forEach(([key, val]) => {
            flatContent[key] = val.text || val.image || '';
          });
        }
        setContent(flatContent);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setContent(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async (key) => {
    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`${BACKEND_URL}/api/admin/content`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          section: 'homepage',
          key: key,
          value: content[key]
        })
      });
      
      if (response.ok) {
        toast.success('Contenu sauvegardé !');
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('admin_token');
      
      for (const [key, value] of Object.entries(content)) {
        await fetch(`${BACKEND_URL}/api/admin/content`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            section: 'homepage',
            key: key,
            value: value
          })
        });
      }
      
      toast.success('Tout le contenu a été sauvegardé !');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const ContentField = ({ label, fieldKey, type = 'text', rows = 1 }) => (
    <div style={{ marginBottom: '24px' }}>
      <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
        {label}
      </label>
      <div style={{ display: 'flex', gap: '12px' }}>
        {type === 'textarea' ? (
          <textarea
            value={content[fieldKey] || ''}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            rows={rows}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              resize: 'vertical'
            }}
          />
        ) : (
          <input
            type={type}
            value={content[fieldKey] || ''}
            onChange={(e) => handleChange(fieldKey, e.target.value)}
            style={{
              flex: 1,
              padding: '12px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px'
            }}
          />
        )}
        <button
          onClick={() => handleSave(fieldKey)}
          disabled={saving}
          style={{
            padding: '12px 16px',
            background: 'var(--brand-dark)',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <Save size={16} />
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <AdminLayout>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px' }}>
          <Loader2 size={32} className="animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ maxWidth: '900px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
              Contenu de la Page d'Accueil
            </h1>
            <p style={{ color: '#666' }}>Modifiez les textes et images de votre homepage</p>
          </div>
          <button
            onClick={handleSaveAll}
            disabled={saving}
            style={{
              padding: '12px 24px',
              background: 'var(--brand-primary)',
              color: 'var(--brand-dark)',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {saving ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
            Tout sauvegarder
          </button>
        </div>

        {/* Section Hero */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--brand-dark)' }}>
            🏠 Section Hero
          </h2>
          <ContentField label="Titre principal" fieldKey="hero_title" />
          <ContentField label="Sous-titre" fieldKey="hero_subtitle" type="textarea" rows={2} />
        </div>

        {/* Section Statistiques */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--brand-dark)' }}>
            📊 Statistiques
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '16px' }}>
            <ContentField label="Stat 1 - Nombre" fieldKey="stat_1_number" />
            <ContentField label="Stat 1 - Label" fieldKey="stat_1_label" />
            <ContentField label="Stat 2 - Nombre" fieldKey="stat_2_number" />
            <ContentField label="Stat 2 - Label" fieldKey="stat_2_label" />
            <ContentField label="Stat 3 - Nombre" fieldKey="stat_3_number" />
            <ContentField label="Stat 3 - Label" fieldKey="stat_3_label" />
            <ContentField label="Stat 4 - Nombre" fieldKey="stat_4_number" />
            <ContentField label="Stat 4 - Label" fieldKey="stat_4_label" />
          </div>
        </div>

        {/* Section Pourquoi nous */}
        <div style={{ background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px', color: 'var(--brand-dark)' }}>
            ✅ Section "Pourquoi Nous Choisir"
          </h2>
          <ContentField label="Titre de la section" fieldKey="why_title" />
          <ContentField label="Sous-titre" fieldKey="why_subtitle" type="textarea" rows={2} />
        </div>
      </div>
    </AdminLayout>
  );
};

export default CMSHomepage;
